import { Section, SectionDescription, SectionHeading } from "@/components/Section";
import { Card } from "@/components/common";
import { useZodForm } from "@/hooks/useZodForm";
import { htmlSerializer } from "@/lib/htmlSerializer";
import { Box, Button, FormControl, FormErrorMessage, FormLabel, Input, Select, Text, Textarea } from "@chakra-ui/react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { AnimatePresence, motion } from "framer-motion";
import useTranslation from "next-translate/useTranslation";
import * as React from "react";
import { z } from "zod";

const MotionBox = motion(Box);

type Topic = "apartments" | "partnerships" | "properties" | "payments" | "employment" | "press" | "other";

const topics: Topic[] = ["apartments", "partnerships", "properties", "payments", "employment", "press", "other"];

const TopicMap: Record<Topic, { email: string; subject: string }> = {
  apartments: {
    email: "booking@joinlifex.com",
    subject: "I have a question about your apartments",
  },
  partnerships: {
    email: "hello@joinlifex.com",
    subject: "I would like to partner up with LifeX",
  },
  properties: {
    email: "realestate@joinlifex.com",
    subject: "I have a property to offer",
  },
  payments: {
    email: "ar@joinlifex.com",
    subject: "I have a question about invoicing / payments",
  },
  employment: {
    email: "hello@joinlifex.com",
    subject: "I would like to work for LifeX",
  },
  press: {
    email: "press@joinlifex.com",
    subject: "I have a press inquiry",
  },
  other: {
    email: "hello@joinlifex.com",
    subject: "Other",
  },
};

export const contactFormSchema = z.object({
  subject: z.string(),
  to: z.string().email(),
  firstName: z
    .string({ required_error: "contact-form.first-name.error.required" })
    .min(1, { message: "contact-form.first-name.error.required" }),
  lastName: z
    .string({ required_error: "contact-form.last-name.error.required" })
    .min(1, { message: "contact-form.last-name.error.required" }),
  email: z
    .string({ required_error: "contact-form.email.error.required" })
    .email({ message: "contact-form.email.error.required" }),
  topic: z.enum(topics as [string, ...string[]], {
    errorMap: () => ({ message: "contact-form.topic.error.required" }),
  }),
  message: z
    .string({ required_error: "contact-form.message.error.required" })
    .min(1, { message: "contact-form.message.error.required" }),
});

export type ContactFormProps = SliceComponentProps<Content.ContactFormSlice>;

const ContactForm = ({ slice }: ContactFormProps): JSX.Element => {
  const isCenterVariant = slice.variation.toLowerCase().includes("center") ? true : false;
  const sectionVariant = slice.variation.toLowerCase().includes("cream") ? "cream" : "white";

  const { t } = useTranslation("common");
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useZodForm({
    schema: contactFormSchema,
  });

  async function onSubmit(data: z.infer<typeof contactFormSchema>) {
    setLoading(true);

    await fetch("/api/sendEmail", {
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    });

    setLoading(false);
    setSubmitted(true);
  }

  return (
    <Section
      variant={sectionVariant}
      position="relative"
      overflow="hidden"
      containerProps={{
        display: "flex",
        flexDirection: "column",
        alignItems: isCenterVariant ? "center" : "flex-start",
      }}
    >
      {slice.primary.title ? <SectionHeading>{slice.primary.title}</SectionHeading> : null}
      <SectionDescription>
        <PrismicRichText field={slice.primary.description} components={htmlSerializer} />
      </SectionDescription>
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card bg="cream" maxW="70ch">
              {slice.primary.successMessage ? (
                <PrismicRichText field={slice.primary.successMessage} components={htmlSerializer} />
              ) : (
                <Text>{t("contact-form.submitted")}</Text>
              )}
            </Card>
          </motion.div>
        ) : (
          <MotionBox
            as="form"
            display="grid"
            w="100%"
            maxW="2xl"
            gridRowGap={5}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormControl isInvalid={Boolean(errors.firstName)}>
              <FormLabel htmlFor="firstName">{t("contact-form.first-name.label")}</FormLabel>
              <Input {...register("firstName")} />
              {errors.firstName && errors.firstName.message ? (
                <FormErrorMessage>{t(errors.firstName.message)}</FormErrorMessage>
              ) : null}
            </FormControl>
            <FormControl isInvalid={Boolean(errors.lastName)}>
              <FormLabel htmlFor="lastName">{t("contact-form.last-name.label")}</FormLabel>
              <Input {...register("lastName")} />
              {errors.lastName && errors.lastName.message ? (
                <FormErrorMessage>{t(errors.lastName.message)}</FormErrorMessage>
              ) : null}
            </FormControl>
            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel htmlFor="email">{t("contact-form.email.label")}</FormLabel>
              <Input type="email" {...register("email")} />
              {errors.email && errors.email.message ? (
                <FormErrorMessage>{t(errors.email.message)}</FormErrorMessage>
              ) : null}
            </FormControl>
            <FormControl isInvalid={Boolean(errors.topic)}>
              <FormLabel htmlFor="topic">{t("contact-form.topic.label")}</FormLabel>
              <Select
                placeholder={t("contact-form.topic.placeholder")}
                {...register("topic")}
                onChange={(event) => {
                  const { email, subject } = TopicMap[event.target.value as Topic];
                  setValue("subject", subject);
                  setValue("to", email);
                  register("topic").onChange(event);
                }}
              >
                <option value="apartments">{t("contact-form.topic.apartments")}</option>
                <option value="partnerships">{t("contact-form.topic.partnerships")}</option>
                <option value="properties">{t("contact-form.topic.properties")}</option>
                <option value="payments">{t("contact-form.topic.payments")}</option>
                <option value="employment">{t("contact-form.topic.employment")}</option>
                <option value="press">{t("contact-form.topic.press")}</option>
                <option value="other">{t("contact-form.topic.other")}</option>
              </Select>
              {errors.topic && errors.topic.message ? (
                <FormErrorMessage>{t(errors.topic.message)}</FormErrorMessage>
              ) : null}
            </FormControl>
            <FormControl isInvalid={Boolean(errors.message)}>
              <FormLabel htmlFor="message">{t("contact-form.message.label")}</FormLabel>
              <Textarea {...register("message")} />
              {errors.message && errors.message.message ? (
                <FormErrorMessage>{t(errors.message.message)}</FormErrorMessage>
              ) : null}
            </FormControl>
            <Button isLoading={loading} justifySelf="flex-end" maxW="max-content" type="submit">
              {t("submit")}
            </Button>
          </MotionBox>
        )}
      </AnimatePresence>
    </Section>
  );
};

export default ContactForm;
